/**
 * Architecture Diagram Component
 * CTO-level AWS enterprise style diagram showing Raindrop integration
 */

export function ArchitectureDiagram() {
  return (
    <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-6">
      <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <span>🏗️</span> System Architecture
      </h3>

      {/* Architecture SVG Diagram */}
      <svg viewBox="0 0 800 500" className="w-full h-auto" style={{ maxHeight: '450px' }}>
        <defs>
          {/* Gradients */}
          <linearGradient id="raindropGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id="vultrGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#007bfc" />
            <stop offset="100%" stopColor="#00d4ff" />
          </linearGradient>
          <linearGradient id="userGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
          <linearGradient id="outputGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#fbbf24" />
          </linearGradient>

          {/* Arrow marker */}
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
          </marker>
          <marker id="arrowheadBlue" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
          </marker>

          {/* Glow filter */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background grid */}
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1e293b" strokeWidth="1" />
        </pattern>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* ===== USER/CLIENT SECTION ===== */}
        <g transform="translate(50, 60)">
          <rect x="0" y="0" width="140" height="80" rx="8" fill="url(#userGrad)" opacity="0.9" />
          <text x="70" y="35" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
            Client App
          </text>
          <text x="70" y="52" textAnchor="middle" fill="white" fontSize="10" opacity="0.8">
            React + Three.js
          </text>
          <text x="70" y="68" textAnchor="middle" fill="white" fontSize="20">
            🖥️
          </text>
        </g>

        {/* Upload arrow */}
        <path d="M 190 100 L 260 100" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" strokeDasharray="5,3" />
        <text x="225" y="90" textAnchor="middle" fill="#94a3b8" fontSize="9">PLY/GLB</text>

        {/* ===== RAINDROP SMART COMPONENTS (CENTRAL) ===== */}
        <g transform="translate(270, 20)">
          {/* Main Raindrop container */}
          <rect x="0" y="0" width="280" height="340" rx="12" fill="#1e1b4b" stroke="url(#raindropGrad)" strokeWidth="2" />
          
          {/* Raindrop header */}
          <rect x="0" y="0" width="280" height="50" rx="12" fill="url(#raindropGrad)" />
          <rect x="0" y="38" width="280" height="12" fill="url(#raindropGrad)" />
          <text x="140" y="32" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
            ☔ Raindrop Smart Components
          </text>

          {/* SmartBuckets */}
          <g transform="translate(20, 65)">
            <rect x="0" y="0" width="115" height="70" rx="6" fill="#312e81" stroke="#6366f1" strokeWidth="1" />
            <text x="57" y="20" textAnchor="middle" fill="#a5b4fc" fontSize="10" fontWeight="bold">
              SmartBuckets
            </text>
            <text x="57" y="35" textAnchor="middle" fill="#818cf8" fontSize="8">
              raw/{'{asset_id}'}
            </text>
            <text x="57" y="47" textAnchor="middle" fill="#818cf8" fontSize="8">
              outputs/{'{job_id}'}
            </text>
            <text x="57" y="59" textAnchor="middle" fill="#c4b5fd" fontSize="14">
              🪣
            </text>
          </g>

          {/* SmartSQL */}
          <g transform="translate(145, 65)">
            <rect x="0" y="0" width="115" height="70" rx="6" fill="#312e81" stroke="#6366f1" strokeWidth="1" />
            <text x="57" y="20" textAnchor="middle" fill="#a5b4fc" fontSize="10" fontWeight="bold">
              SmartSQL
            </text>
            <text x="57" y="35" textAnchor="middle" fill="#818cf8" fontSize="8">
              assets, jobs
            </text>
            <text x="57" y="47" textAnchor="middle" fill="#818cf8" fontSize="8">
              job_stats
            </text>
            <text x="57" y="59" textAnchor="middle" fill="#c4b5fd" fontSize="14">
              🗄️
            </text>
          </g>

          {/* SmartMemory */}
          <g transform="translate(20, 145)">
            <rect x="0" y="0" width="115" height="70" rx="6" fill="#312e81" stroke="#6366f1" strokeWidth="1" />
            <text x="57" y="20" textAnchor="middle" fill="#a5b4fc" fontSize="10" fontWeight="bold">
              SmartMemory
            </text>
            <text x="57" y="35" textAnchor="middle" fill="#818cf8" fontSize="8">
              Fab Profiles
            </text>
            <text x="57" y="47" textAnchor="middle" fill="#818cf8" fontSize="8">
              web | quest | showcase
            </text>
            <text x="57" y="59" textAnchor="middle" fill="#c4b5fd" fontSize="14">
              🧠
            </text>
          </g>

          {/* SmartInference */}
          <g transform="translate(145, 145)">
            <rect x="0" y="0" width="115" height="70" rx="6" fill="#312e81" stroke="#6366f1" strokeWidth="1" />
            <text x="57" y="20" textAnchor="middle" fill="#a5b4fc" fontSize="10" fontWeight="bold">
              SmartInference
            </text>
            <text x="57" y="35" textAnchor="middle" fill="#818cf8" fontSize="8">
              Plan Generation
            </text>
            <text x="57" y="47" textAnchor="middle" fill="#818cf8" fontSize="8">
              Deterministic
            </text>
            <text x="57" y="59" textAnchor="middle" fill="#c4b5fd" fontSize="14">
              🤖
            </text>
          </g>

          {/* Orchestration layer */}
          <g transform="translate(20, 230)">
            <rect x="0" y="0" width="240" height="45" rx="6" fill="#4c1d95" stroke="#8b5cf6" strokeWidth="1" />
            <text x="120" y="18" textAnchor="middle" fill="#e9d5ff" fontSize="10" fontWeight="bold">
              ⚡ Job Orchestration Layer
            </text>
            <text x="120" y="33" textAnchor="middle" fill="#c4b5fd" fontSize="8">
              Queue • Callbacks • State Management
            </text>
          </g>

          {/* Data flow arrows inside Raindrop */}
          <path d="M 77 135 L 77 145" stroke="#6366f1" strokeWidth="1" markerEnd="url(#arrowheadBlue)" />
          <path d="M 202 135 L 202 145" stroke="#6366f1" strokeWidth="1" markerEnd="url(#arrowheadBlue)" />
          <path d="M 77 215 L 77 230" stroke="#6366f1" strokeWidth="1" markerEnd="url(#arrowheadBlue)" />
          <path d="M 202 215 L 202 230" stroke="#6366f1" strokeWidth="1" markerEnd="url(#arrowheadBlue)" />
        </g>

        {/* Arrow from Client to Raindrop */}
        <path d="M 190 100 L 270 100" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />

        {/* ===== VULTR COMPUTE ===== */}
        <g transform="translate(600, 120)">
          <rect x="0" y="0" width="150" height="140" rx="8" fill="#0c4a6e" stroke="url(#vultrGrad)" strokeWidth="2" />
          <text x="75" y="25" textAnchor="middle" fill="#7dd3fc" fontSize="12" fontWeight="bold">
            Vultr Cloud GPU
          </text>
          <text x="75" y="45" textAnchor="middle" fill="#38bdf8" fontSize="10">
            Conversion Worker
          </text>
          
          {/* Worker tasks */}
          <text x="75" y="70" textAnchor="middle" fill="#0ea5e9" fontSize="8">• Decimate mesh</text>
          <text x="75" y="85" textAnchor="middle" fill="#0ea5e9" fontSize="8">• UV unwrap</text>
          <text x="75" y="100" textAnchor="middle" fill="#0ea5e9" fontSize="8">• Bake textures</text>
          <text x="75" y="115" textAnchor="middle" fill="#0ea5e9" fontSize="8">• Export GLB</text>
          <text x="75" y="132" textAnchor="middle" fill="white" fontSize="16">⚙️</text>
        </g>

        {/* Arrow from Raindrop to Vultr */}
        <path d="M 550 190 L 600 190" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
        <text x="575" y="180" textAnchor="middle" fill="#94a3b8" fontSize="9">plan.json</text>

        {/* Arrow from Vultr back to Raindrop */}
        <path d="M 600 230 L 550 230" stroke="#22d3ee" strokeWidth="2" markerEnd="url(#arrowhead)" strokeDasharray="5,3" />
        <text x="575" y="245" textAnchor="middle" fill="#22d3ee" fontSize="9">callback</text>

        {/* ===== OUTPUT SECTION ===== */}
        <g transform="translate(50, 320)">
          <rect x="0" y="0" width="140" height="100" rx="8" fill="url(#outputGrad)" opacity="0.9" />
          <text x="70" y="25" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
            Fabricated Asset
          </text>
          <text x="70" y="45" textAnchor="middle" fill="white" fontSize="9" opacity="0.9">
            ✓ Optimized GLB
          </text>
          <text x="70" y="58" textAnchor="middle" fill="white" fontSize="9" opacity="0.9">
            ✓ Baked Textures
          </text>
          <text x="70" y="71" textAnchor="middle" fill="white" fontSize="9" opacity="0.9">
            ✓ Game-Ready
          </text>
          <text x="70" y="90" textAnchor="middle" fill="white" fontSize="20">
            ✨
          </text>
        </g>

        {/* Arrow from Raindrop to Output */}
        <path d="M 270 320 L 190 370" stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrowhead)" />
        <text x="240" y="355" textAnchor="middle" fill="#fbbf24" fontSize="9">optimized.glb</text>

        {/* ===== A/B VIEWER ===== */}
        <g transform="translate(600, 320)">
          <rect x="0" y="0" width="150" height="100" rx="8" fill="#1e293b" stroke="#475569" strokeWidth="1" />
          <text x="75" y="25" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="bold">
            A/B Viewer
          </text>
          <rect x="15" y="40" width="50" height="35" rx="4" fill="#334155" />
          <text x="40" y="62" textAnchor="middle" fill="#94a3b8" fontSize="8">Raw</text>
          <rect x="85" y="40" width="50" height="35" rx="4" fill="#065f46" />
          <text x="110" y="62" textAnchor="middle" fill="#6ee7b7" fontSize="8">Fab</text>
          <text x="75" y="90" textAnchor="middle" fill="#64748b" fontSize="8">Split Compare</text>
        </g>

        {/* Arrow from Output to Viewer */}
        <path d="M 190 370 L 600 370" stroke="#64748b" strokeWidth="1" strokeDasharray="3,3" />

        {/* ===== METRICS BADGE ===== */}
        <g transform="translate(320, 400)">
          <rect x="0" y="0" width="180" height="60" rx="8" fill="#0f172a" stroke="#334155" strokeWidth="1" />
          <text x="90" y="20" textAnchor="middle" fill="#94a3b8" fontSize="9">METRICS</text>
          <text x="45" y="40" textAnchor="middle" fill="#10b981" fontSize="10">-85% tris</text>
          <text x="135" y="40" textAnchor="middle" fill="#10b981" fontSize="10">-70% size</text>
          <text x="90" y="55" textAnchor="middle" fill="#6366f1" fontSize="8">Powered by Raindrop</text>
        </g>

        {/* Legend */}
        <g transform="translate(600, 440)">
          <text x="0" y="12" fill="#64748b" fontSize="9">
            ━━ Data Flow
          </text>
          <text x="0" y="28" fill="#64748b" fontSize="9">
            ┄┄ Async Callback
          </text>
        </g>
      </svg>

      {/* Key Benefits */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl mb-1">🪣</div>
          <div className="text-xs text-slate-400">SmartBuckets</div>
          <div className="text-xs text-fab-accent">Asset Storage</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl mb-1">🗄️</div>
          <div className="text-xs text-slate-400">SmartSQL</div>
          <div className="text-xs text-fab-accent">Job Tracking</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl mb-1">🧠</div>
          <div className="text-xs text-slate-400">SmartMemory</div>
          <div className="text-xs text-fab-accent">Profile Config</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl mb-1">🤖</div>
          <div className="text-xs text-slate-400">SmartInference</div>
          <div className="text-xs text-fab-accent">Plan Generation</div>
        </div>
      </div>
    </div>
  );
}
